import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import { notFound, useReadOnlyMode } from '../../routes';
import {
  fromAccounts,
  fromBillingInformation,
  fromOrder,
  fromPaymentMethod,
} from '../../store/rootReducer';
import { MAX_TOTAL_AMOUNT } from '../../utils/utils';
import PaymentMethodSelection from './payment-method/PaymentMethodSelection';
import { PaymentMethodCards } from './payment-method/PaymentMethodCards';
import { PaymentMethodType } from '../../models';
import { useAppDispatch } from '../../store';
import { useLaunchDarkly } from '../../launchdarkly';
import { useConfig } from '../../config';
import { checkout } from '../../layout/routes';
import { BillingInformationComponent } from '../index';
import { LoadingProgress } from '../../layout';
import { CardInformation } from './payment-method/CardInformation';
import { page } from '../../analytics';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    width: '100%',
    borderRadius: '.5rem',
    backgroundColor: theme.palette.background.paper,
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(1),
  },
  rootRedesign: {
    width: '100%',
    backgroundColor: 'transparent',
    // Payment & billing v1 mock form styling, scoped to the billing block so
    // other pages using BillingInformation keep their existing look.
    '& #billing-information .MuiTypography-h3, & #billing-information .MuiTypography-h6': {
      fontSize: 16,
      fontWeight: 600,
      color: '#16141d',
    },
    '& #billing-information .MuiTypography-subtitle2': {
      fontSize: 14,
      color: '#5b6661',
      fontStyle: 'normal',
    },
    '& #billing-information .MuiTypography-body1': {
      fontSize: 14,
      fontWeight: 600,
      color: '#16141d',
      marginBottom: 8,
    },
    // The form's trailing <br /> and bottom padding pad the gap above the terms checkbox.
    '& #billing-information-form > br': {
      display: 'none',
    },
    '& #billing-information-form-layout': {
      paddingBottom: 0,
    },
    '& #billing-information .MuiOutlinedInput-root': {
      minHeight: 48,
      borderRadius: 10,
      fontSize: 15,
      color: '#16141d',
      backgroundColor: '#fff',
      transition: 'border-color .15s ease, box-shadow .15s ease',
    },
    '& #billing-information .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e1e7e4',
    },
    '& #billing-information .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#cdd6d1',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(15,132,97,.15)',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0f8461',
      borderWidth: 1,
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e5484d',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-error': {
      backgroundColor: '#fff6f6',
    },
    // Chrome autofill paints only the inner <input>, leaving a white ring on
    // the rounded MuiOutlinedInput wrapper. Tint the whole wrapper with the
    // autofill blue (#e8f0fe) so the fill covers the full input like the mock.
    '& #billing-information .MuiOutlinedInput-root:has(input:-webkit-autofill)': {
      backgroundColor: '#e8f0fe',
    },
    '& #billing-information input:-webkit-autofill, & #billing-information input:-webkit-autofill:hover, & #billing-information input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 1000px #e8f0fe inset',
      WebkitTextFillColor: '#16141d',
      caretColor: '#16141d',
      borderRadius: 'inherit',
    },
  },
  title: {
    marginTop: theme.spacing(2.5),
    paddingBottom: '1rem',
  },
  subTitle: {
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  checkoutButton: {
    marginTop: theme.spacing(2),
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.background.paper,
  },
  paymentMethodSelection: {
    marginBottom: theme.spacing(5),
  },
  subscribeBtn: {
    fontWeight: 500,
    fontSize: 14,
  },
}));

interface PaymentInformationSectionProps {
  accountId: number;
  enableSave: boolean;
  availableRenewal?: boolean;
  hasMonthlyItem?: boolean;
  hasPaidSubscription?: boolean;
  enableDeleteCard?: boolean;
  handleSelectPaymentMethod: (paymentMethod: PaymentMethodType) => void;
  setTriggerOptInSelfServe?: Function;
  showCardInformation?: boolean;
  /** Payment & billing v1 redesign: card-style method selector, transparent container */
  redesign?: boolean;
}

export const PaymentInformationSection = forwardRef((
  props: PaymentInformationSectionProps,
  ref,
) => {
  const { config } = useConfig();
  if (!config) return <></>;
  const location = useLocation();
  const isCheckoutPage = location.pathname.includes(checkout.path);
  const classes = useStyles();
  const {
    accountId,
    enableSave,
    availableRenewal,
    setTriggerOptInSelfServe,
    hasMonthlyItem,
    handleSelectPaymentMethod,
    hasPaidSubscription,
    enableDeleteCard,
    showCardInformation,
    redesign,
  } = props;
  const { flags } = useLaunchDarkly();
  const intl = useIntl();
  const billingRef = useRef<any>(null);
  const cardRef = useRef<any>(null);

  const pmLoading = useSelector(fromPaymentMethod.selectLoading);
  const orderLoading = useSelector(fromOrder.selectLoading);
  const previewsTotal = useSelector(fromOrder.selectPreviewsTotal);
  const cardInformationInvalid = useSelector(fromPaymentMethod.isCardInformationInvalid);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const successEmail = <a target="_blank" href="mailto: success@katalon.com" rel="noreferrer">success@katalon.com</a>;
  const selectedPaymentMethod = useSelector(fromPaymentMethod.selectSelectedPaymentMethod);
  const isPublicDomain = useSelector(fromAccounts.selectIsPublicDomain);
  const defaultPaymentMethod = useSelector(fromPaymentMethod.selectDefaultPaymentMethod);
  const currentPaymentMethod = useSelector(fromPaymentMethod.selectOneByAccountId(
    Number(accountId),
  ));
  const currentBillingInformation = useSelector(fromBillingInformation.selectOneByAccountId(
    Number(accountId),
  ));
  const isReadOnly = useReadOnlyMode();

  const isEnableSave = enableSave && (flags?.showSaveCardAndBillingInfoBtn || (!isCheckoutPage
    || currentPaymentMethod !== undefined || currentBillingInformation !== undefined));

  useEffect(() => {
    if (!defaultPaymentMethod && !currentPaymentMethod) {
      dispatch(fromPaymentMethod.doSetSelectedPaymentMethod(PaymentMethodType.CREDIT_CARD));
    }
  }, [defaultPaymentMethod, currentPaymentMethod]);

  useEffect(() => {
    dispatch(fromOrder.updateSaveCardAndBillingInfoBtnEnabled(isEnableSave));
  }, [isEnableSave]);
  /**
   * Check if bank transfer is not available:
   * - Checkout monthly item is not available for bank transfer
   * - Account has existing paid subscription is not available for bank transfer
   * - Switch from direct to self-serve is not available
   * - BankTransferEnabled feature flag is turn off or this account is not in the white list
   * - This user email is public domain
   * - Not in restricted for this region
   */
  const notAvailableBankTransfer = () => {
    if (defaultPaymentMethod === PaymentMethodType.AWS_MARKETPLACE) {
      return false;
    }

    // has monthly item or existing paid subscription
    if (hasMonthlyItem || hasPaidSubscription) {
      return true;
    }

    // Switch from direct to self-serve
    if (!availableRenewal) {
      return true;
    }

    if (!flags?.bankTransferEnabled) {
      // Bank transfer is not enabled
      return true;
    }

    return isPublicDomain;
  };

  const initializePage = async () => {
    page();
  };

  useEffect(() => {
    initializePage()
      .catch(() => {
        // do nothing
      });
  }, []);

  const isExceededMaxAmount = () => previewsTotal > MAX_TOTAL_AMOUNT;

  useEffect(() => {
    if (!orderLoading && isExceededMaxAmount()) {
      enqueueSnackbar(
        intl.formatMessage(
          { id: 'payment_status.total.exceeded.limit' },
          { email: successEmail },
        ),
        { variant: 'error' },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewsTotal]);

  useEffect(() => {
    if (notAvailableBankTransfer()) {
      dispatch(fromPaymentMethod.doSetSelectedPaymentMethod(PaymentMethodType.CREDIT_CARD));
    }
  }, [notAvailableBankTransfer()]);

  useImperativeHandle(ref, () => ({
    isEnableSave: () => !isEnableSave,
    saveBillingInformation: () => {
      if (billingRef.current) {
        billingRef.current.saveBillingInformation();
      }
    },
    validateAndSaveBillingInformation: (): Promise<boolean> => (
      billingRef.current?.validateAndSave() ?? Promise.resolve(true)
    ),
    saveCardInformation: () => {
      if (cardRef.current) {
        cardRef.current.saveCardInformation();
      }
    },
  }));

  const handleOptInSelfServe = () => {
    if (setTriggerOptInSelfServe) {
      setTriggerOptInSelfServe(true);
    }
    cardRef.current?.saveCardInformation();
    billingRef.current?.renewOnline();
  };

  if (!accountId || Number.isNaN(accountId)) return <Navigate to={notFound.path} replace />;

  return (
    <Paper
      id="payment-information-section"
      className={redesign ? classes.rootRedesign : classes.root}
      elevation={0}
    >
      {redesign
        ? !notAvailableBankTransfer() && (
          <PaymentMethodCards
            showBankTransfer
            handleSelectPaymentMethod={handleSelectPaymentMethod}
          />
        )
        : !notAvailableBankTransfer()
        && (
        <Grid id="payment-method-selection" className={classes.paymentMethodSelection}>
          <PaymentMethodSelection
            handleSelectPaymentMethod={handleSelectPaymentMethod}
          />
        </Grid>
        )}
      <Grid>
        {(notAvailableBankTransfer() || selectedPaymentMethod === PaymentMethodType.CREDIT_CARD)
          && showCardInformation
          && (
            <CardInformation
              ref={cardRef}
              accountId={accountId}
              enableSave={isEnableSave}
              enableDelete={enableDeleteCard ?? false}
            />
          )}
      </Grid>
      <BillingInformationComponent
        ref={billingRef}
        accountId={accountId}
        enableSave={isEnableSave}
        currentBillingInformation={currentBillingInformation}
        redesign={redesign}
      />
      {!enableSave && (
        <Button
          variant="contained"
          size="large"
          color="primary"
          disabled={cardInformationInvalid || orderLoading || isReadOnly}
          onClick={handleOptInSelfServe}
          className={classes.subscribeBtn}
        >
          {intl.formatMessage({ id: 'direct.selfserve.review_online' })}
        </Button>
      )}
      {pmLoading && (<LoadingProgress fullscreen />)}
    </Paper>
  );
});
